import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useTourBuilder } from "@/contexts/TourBuilderContext";
import { ActiveToursList } from "@/components/tour/ActiveToursList";
import { UserGuidesList } from "@/components/guides/UserGuidesList";
import { UserGuideUploadModal } from "@/components/guides/UserGuideUploadModal";
import { VideoGuidesList } from "@/components/guides/VideoGuidesList";
import { VideoGuideAddModal } from "@/components/guides/VideoGuideAddModal";
import { FAQList } from "@/components/guides/FAQList";
import { FAQSectionModal } from "@/components/guides/FAQSectionModal";
import { FAQModal } from "@/components/guides/FAQModal";
import { Settings, Upload, HelpCircle, ChevronDown, Video } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Documentation() {
  const { isSystemAdmin } = useSimpleRole();
  const { setIsOpen } = useTourBuilder();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [faqSectionModalOpen, setFaqSectionModalOpen] = useState(false);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Admin actions for the header
  const headerActions = isSystemAdmin ? (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={() => setIsOpen(true)} className="flex items-center gap-2">
        <Settings className="h-4 w-4" />
        Tour Builder
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            Manage FAQ
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setFaqSectionModalOpen(true)}>
            Add Section
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setFaqModalOpen(true)}>
            Add FAQ
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="outline" onClick={() => setUploadModalOpen(true)} className="flex items-center gap-2 text-black hover:opacity-80" style={{
        backgroundColor: "white"
      }}>
        <Upload className="h-4 w-4" />
        <span>Upload Guide</span>
      </Button>
      <Button variant="outline" onClick={() => setVideoModalOpen(true)} className="flex items-center gap-2 text-black hover:opacity-80" style={{
        backgroundColor: "white"
      }}>
        <Video className="h-4 w-4" />
        <span>Add Video</span>
      </Button>
    </div>
  ) : null;

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.documentation} actions={headerActions} />
          <div className="p-4 sm:p-6 lg:p-12 xl:px-16" style={{ paddingTop: "8.5rem" }}>
            
            {/* Content Sections - Vertical Layout */}
            <div className="w-full max-w-none space-y-12 lg:space-y-16">
              
              {/* Available Tours Section */}
              <section id="tours">
                <div className="mb-6 lg:mb-8">
                  <h2 className="text-2xl lg:text-3xl font-semibold mb-2">Available Tours</h2>
                  <p className="text-muted-foreground text-sm lg:text-base">
                    Choose from our interactive tours to learn different parts of the platform
                  </p>
                </div>
                <ActiveToursList />
              </section>

              {/* User Guides Section */}
              <section id="guides">
                <div className="mb-6 lg:mb-8">
                  <h2 className="text-2xl lg:text-3xl font-semibold mb-2">User Guides</h2>
                  <p className="text-muted-foreground text-sm lg:text-base">
                    Downloadable PDF guides and documentation to help you get the most out of the platform.
                  </p>
                </div>
                <UserGuidesList />
              </section>

              {/* Videos Section */}
              <section id="videos">
                <div className="mb-6 lg:mb-8">
                  <h2 className="text-2xl lg:text-3xl font-semibold mb-2">Videos</h2>
                  <p className="text-muted-foreground text-sm lg:text-base">
                    Watch short video walkthroughs of the platform.
                  </p>
                </div>
                <VideoGuidesList />
              </section>

              {/* FAQ Section */}
              <section id="faq">
                <div className="mb-6 lg:mb-8">
                  <h2 className="text-2xl lg:text-3xl font-semibold mb-2">Frequently Asked Questions</h2>
                  <p className="text-muted-foreground text-sm lg:text-base">
                    Quick answers to common questions, organized by topic
                  </p>
                </div>
                <FAQList />
              </section>

            </div>
          </div>
        </main>
      </div>
      
      <UserGuideUploadModal open={uploadModalOpen} onOpenChange={setUploadModalOpen} />
      <FAQSectionModal open={faqSectionModalOpen} onOpenChange={setFaqSectionModalOpen} />
      <FAQModal open={faqModalOpen} onOpenChange={setFaqModalOpen} />
      <VideoGuideAddModal open={videoModalOpen} onOpenChange={setVideoModalOpen} />
    </SidebarProvider>
  );
}